import { useEffect, useState, useRef, useCallback } from "react";
import { ChatList } from "@/components/global/Chat/components/chat/ChatList";
import { ReservedUserIds, UserData } from "@/entities/users/types";
import { useMessaging } from "@/entities/messaging/hooks";
import {
  ChatMessage,
  MessagePayload,
  MAX_MESSAGES,
  alertsUser,
  notificationsUser
} from "@/entities/messaging/types";
import { getCurrentTimeUTC } from "@/utils/datetime";
import { STORED_MESSAGE_ID_PREFIX, useChat } from "./ChatContext";
import { last } from "lodash";
import { useDraftEmailRegenerate } from "@/components/global/Chat/components/chat/useDraftEmailRegenerate";
import { useDraftEmailSelectedVersion } from "@/components/global/Chat/components/chat/useDraftEmailSelectedVersion";

interface Props {
  selectedUser: UserData;
  dreamerUser: UserData;
  mentorUser: UserData;
  alertsUser?: UserData;
  notificationsUser?: UserData;
  messageFromHeader?: string;
  setMessageFromHeader?: any;
  setChatDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  chatDrawerOpen: boolean;
  handleNotification: (messages: ChatMessage[]) => void;
  focusDraftId: string | null;
  onFocusDraftHandled: () => void;
}

const LIVE_NOTIFICATION_GRACE_SECONDS = 5;

const ChatController: React.FC<Props> = ({
  selectedUser,
  dreamerUser,
  mentorUser,
  messageFromHeader,
  setMessageFromHeader,
  setChatDrawerOpen,
  chatDrawerOpen,
  handleNotification,
  focusDraftId,
  onFocusDraftHandled
}: Props) => {
  const {
    addMessages,
    setPendingUserMessage,
    setThoughtMessage,
    setPendingRequestId,
    onResponseReceived,
    pendingRequestId,
    pendingUserMessage,
    setIsCancelling,
    addStoppedRequestId,
    markResponsesStoppedByRequestId,
    markLastUserMessageStopped,
    setWaitingForMessage,
    stoppedRequestIds,
    removeMessage,
    dismissFailedAnswer,
    setRetryingQuestionId
  } = useChat();
  const { messages, sendMessageViaWebSocket, cancelPendingRequest } =
    useMessaging(
      (thought: string) => {
        setThoughtMessage(thought);
      },
      (requestId: string) => {
        onResponseReceived(requestId);
      },
      // A retry started anywhere, Slack or another tab included, replaces the
      // question's error card with thinking at once rather than when the
      // answer lands.
      (questionId: string) => {
        dismissFailedAnswer(questionId);
        setRetryingQuestionId(questionId);
      }
    );

  const [lastMentorMessageId, setLastMentorMessageId] = useState("");
  const processedIdsRef = useRef<Set<string>>(new Set());
  const notificationWindowStartRef = useRef(
    getCurrentTimeUTC() - LIVE_NOTIFICATION_GRACE_SECONDS
  );
  const lastSentRequestIdRef = useRef<string | null>(null);

  const sendMessageToServer = useCallback(
    (msg: ChatMessage) => {
      const messageReq = {
        message: msg.message
      };
      const requestId = sendMessageViaWebSocket(messageReq);
      lastSentRequestIdRef.current = requestId;
      setPendingRequestId(requestId);
      setPendingUserMessage({ ...msg, requestId });
    },
    [sendMessageViaWebSocket, setPendingRequestId, setPendingUserMessage]
  );

  const cancellingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleStopResponse = useCallback(() => {
    setIsCancelling(true);
    const requestIdToCancel = pendingRequestId ?? lastSentRequestIdRef.current;

    if (requestIdToCancel) {
      addStoppedRequestId(requestIdToCancel);
      cancelPendingRequest(requestIdToCancel);
      markResponsesStoppedByRequestId(requestIdToCancel);
    }

    // Mark the last user message as stopped
    markLastUserMessageStopped(selectedUser.id);

    // If there's a pending user message that hasn't been echoed by server yet,
    // convert it to a real message marked as stopped
    if (pendingUserMessage) {
      addMessages([
        {
          ...pendingUserMessage,
          requestId: requestIdToCancel ?? undefined,
          isStopped: true
        }
      ]);
    }

    // Always clear waiting state regardless of requestId
    setPendingRequestId(null);
    setPendingUserMessage(null);
    setThoughtMessage("");
    setWaitingForMessage(false);

    // Keep spinner visible briefly so the user sees the cancel is processing
    if (cancellingTimerRef.current) {
      clearTimeout(cancellingTimerRef.current);
    }
    cancellingTimerRef.current = setTimeout(() => {
      setIsCancelling(false);
    }, 500);
  }, [
    pendingRequestId,
    pendingUserMessage,
    selectedUser.id,
    cancelPendingRequest,
    setIsCancelling,
    addStoppedRequestId,
    markResponsesStoppedByRequestId,
    markLastUserMessageStopped,
    addMessages,
    setPendingRequestId,
    setPendingUserMessage,
    setThoughtMessage,
    setWaitingForMessage
  ]);

  const handleRetry = useCallback(
    (originalMessage: string) => {
      sendMessageToServer({
        id: new Date().getTime().toString(),
        user: selectedUser,
        message: originalMessage,
        timestamp: getCurrentTimeUTC().toString()
      });
    },
    [sendMessageToServer, selectedUser]
  );

  // Re-asks a question whose answer failed. The question stays as it is on
  // screen: the server answers from its stored copy and echoes no new one, so
  // the error card gives way to thinking and then to the answer (or to a new
  // card if this attempt fails too).
  const handleRetryFailedAnswer = useCallback(
    (question: ChatMessage, errorMessageId: string) => {
      if (!question.id.startsWith(STORED_MESSAGE_ID_PREFIX)) {
        // Resent as a new question, which shows its own pending copy.
        removeMessage(question.id);
        removeMessage(errorMessageId);
        handleRetry(question.message);
        return;
      }
      const requestId = sendMessageViaWebSocket({
        message: question.message,
        retryMessageId: question.id
      });
      if (!requestId) return;
      removeMessage(errorMessageId);
      lastSentRequestIdRef.current = requestId;
      setRetryingQuestionId(question.id);
      setPendingRequestId(requestId);
    },
    [
      handleRetry,
      removeMessage,
      sendMessageViaWebSocket,
      setPendingRequestId,
      setRetryingQuestionId
    ]
  );

  const { regenerateDraft, regeneratingMessageIds } = useDraftEmailRegenerate();
  const selectDraftVersion = useDraftEmailSelectedVersion();

  const getUserById = useCallback(
    (userId: string) => {
      switch (userId) {
        case ReservedUserIds.DREAMER_CONVERSATIONAL_ID:
          return dreamerUser;
        case ReservedUserIds.MENTOR_CONVERSATIONAL_ID:
          return mentorUser;
        case ReservedUserIds.ALERTS_CONVERSATIONAL_ID:
          return alertsUser;
        case ReservedUserIds.NOTIFICATIONS_CONVERSATIONAL_ID:
          return notificationsUser;
        default:
          return selectedUser;
      }
    },
    [dreamerUser, mentorUser, selectedUser]
  );

  useEffect(() => {
    if (!messages || messages.length === 0) return;

    // Keyed by everything about a card that changes what the chat renders: a
    // regenerate re-sends a message this controller has already forwarded,
    // carrying one more draft version and a selection pointing at it, and that
    // redelivery has to reach the chat while an unchanged one stays filtered
    // out. The selection is part of the key rather than implied by the version
    // count so that a redelivery moving only the selection is not mistaken for
    // an unchanged one.
    const processedKey = (msg: MessagePayload) =>
      `${msg.id}:${msg.emailDraft?.versions.length ?? 0}:${
        msg.emailDraft?.selectedVersion ?? 0
      }`;

    const newRawMessages = messages.filter(
      (msg) => !processedIdsRef.current.has(processedKey(msg))
    );
    if (newRawMessages.length === 0) return;

    newRawMessages.forEach((msg) =>
      processedIdsRef.current.add(processedKey(msg))
    );

    // Prevent unbounded growth of the processed IDs set
    if (processedIdsRef.current.size > MAX_MESSAGES) {
      const idsArray = Array.from(processedIdsRef.current);
      processedIdsRef.current = new Set(idsArray.slice(-MAX_MESSAGES));
    }

    const newMessages: ChatMessage[] = newRawMessages
      .filter(
        (msg) => msg.fromUserId !== ReservedUserIds.SYSTEM_CONVERSATIONAL_ID
      )
      .map(
        ({
          id,
          fromUserId,
          message,
          timestamp,
          isStarred,
          isLiked,
          isRead,
          requestId,
          tabularMetadata,
          isStopped,
          isSchedulable,
          isReplayable,
          savedReportId,
          scheduleId,
          reportName,
          content,
          emailDraft,
          isError
        }) => ({
          id,
          user: getUserById(fromUserId),
          message,
          timestamp,
          isStarred,
          isLiked,
          isRead,
          requestId,
          tabularMetadata,
          isStopped,
          isSchedulable,
          isReplayable,
          savedReportId,
          scheduleId,
          reportName,
          content,
          emailDraft,
          isError,
          isStoppedResponse: requestId
            ? stoppedRequestIds.has(requestId)
            : undefined
        })
      );

    if (newMessages.length === 0) return;

    // Filter unread push messages (alerts + notifications) for notifications
    const pushMessages = newMessages.filter(
      (msg) =>
        msg.user !== undefined &&
        (msg.user.id === ReservedUserIds.ALERTS_CONVERSATIONAL_ID ||
          msg.user.id === ReservedUserIds.NOTIFICATIONS_CONVERSATIONAL_ID) &&
        !msg.isRead
    );

    const livePushMessages = pushMessages.filter((msg) => {
      const numericTimestamp = parseInt(msg.timestamp, 10);
      return (
        Number.isFinite(numericTimestamp) &&
        numericTimestamp >= notificationWindowStartRef.current
      );
    });

    if (livePushMessages.length > 0) {
      const incomingLastMentorMessageId = last(livePushMessages)!.id;
      if (incomingLastMentorMessageId !== lastMentorMessageId) {
        handleNotification(livePushMessages);
        setLastMentorMessageId(incomingLastMentorMessageId);
      }
    }

    // Mark user messages as stopped if their requestId was cancelled
    const annotatedMessages = newMessages.map((msg) => {
      if (
        msg.requestId &&
        stoppedRequestIds.has(msg.requestId) &&
        msg.user?.id === selectedUser.id
      ) {
        return { ...msg, isStopped: true };
      }
      return msg;
    });

    addMessages(annotatedMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, getUserById]);

  // Bumped per header send so the panel can return to the Chat tab, where the answer lands.
  const [headerSendCount, setHeaderSendCount] = useState(0);

  useEffect(() => {
    if (messageFromHeader && messageFromHeader !== "") {
      sendMessageToServer({
        id: `panel-message-${new Date().getTime()}`,
        user: selectedUser,
        message: messageFromHeader,
        timestamp: getCurrentTimeUTC().toString()
      });
      setHeaderSendCount((count) => count + 1);
      setMessageFromHeader("");
    }
  }, [
    messageFromHeader,
    sendMessageToServer,
    setMessageFromHeader,
    selectedUser
  ]);

  return (
    <ChatList
      selectedUser={selectedUser}
      sendMessageCallback={sendMessageToServer}
      onStopResponse={handleStopResponse}
      onRetry={handleRetry}
      onRetryFailedAnswer={handleRetryFailedAnswer}
      onRegenerateDraft={regenerateDraft}
      onSelectDraftVersion={selectDraftVersion}
      regeneratingDraftMessageIds={regeneratingMessageIds}
      setChatDrawerOpen={setChatDrawerOpen}
      chatDrawerOpen={chatDrawerOpen}
      focusDraftId={focusDraftId}
      onFocusDraftHandled={onFocusDraftHandled}
      headerSendCount={headerSendCount}
    />
  );
};

export default ChatController;
